// buildHotelSearchParams — pure assembly of hotel-search request parameters.
//
// Extracted verbatim from `AIChatBot.js` (module-level `buildHotelSearchParams`)
// as part of the behavior-preserving refactor (issue #240). Given the collected
// flow params, the derived trip context, and the trip data, it produces the
// `{ destination, city, checkIn, checkOut, locationHint, brand }` shape passed
// to the hotel recommendation service.
//
// The `new Date(tripData.startDate/endDate)` calls are input-derived (they
// format the trip's own dates, not the wall-clock "now"), so no clock injection
// is required. Defaults are preserved exactly ('the destination', 'Unknown City',
// 'city center', 'no preference', and `null` check-in/out when dates are absent).
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * @typedef {import('./deriveTripContext').TripContext} TripContext
 */

/**
 * @typedef {Object} HotelSearchParams
 * @property {string} destination
 * @property {string} city
 * @property {string|null} checkIn
 * @property {string|null} checkOut
 * @property {string} locationHint
 * @property {string} brand
 */

/**
 * Builds the hotel-search request params from collected inputs.
 *
 * @param {Object} finalParams - Collected flow params (locationHint, brand, budget).
 * @param {TripContext} ctx - The derived trip context.
 * @param {Object|null|undefined} tripData - The trip record (may be missing).
 * @returns {HotelSearchParams} The assembled hotel-search parameters.
 */
export function buildHotelSearchParams(finalParams, ctx, tripData) {
  const destination = ctx.destination || 'the destination';
  const city = ctx.city || ctx.destination || tripData?.tripName || 'Unknown City';
  const checkIn = tripData?.startDate
    ? new Date(tripData.startDate).toISOString().split('T')[0]
    : null;
  const checkOut = tripData?.endDate
    ? new Date(tripData.endDate).toISOString().split('T')[0]
    : null;
  const locationHint = finalParams.locationHint || 'city center';
  const brand = finalParams.brand || 'no preference';

  return { destination, city, checkIn, checkOut, locationHint, brand };
}

export default buildHotelSearchParams;
